import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, Mail } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", message: "" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="mds-container pt-14 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <span className="mds-badge mds-badge-primary"><Mail size={14} /> Say hi</span>
          <h1 className="font-heading font-semibold text-4xl sm:text-5xl mt-4 tracking-tight leading-tight">
            Questions, requests, or a service I've missed?
          </h1>
          <p className="text-[#4A5D54] mt-4 leading-relaxed text-lg">
            Drop me a note. I read every message and try to reply within a couple of days.
          </p>
          <div className="mt-10 p-6 rounded-2xl border border-[#E5E3DB] bg-white">
            <h3 className="font-heading font-semibold text-lg">A few pointers</h3>
            <ul className="mt-3 space-y-2 text-[#4A5D54] text-sm">
              <li>• I don't review services I haven't personally used for at least a month.</li>
              <li>• If you found a bug or broken link — please include the page URL.</li>
              <li>• PR / partnership pitches: happy to consider, but I only recommend what I use.</li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="mds-card p-8" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-[#1C2321]">Your name</span>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mds-input mt-2"
                  placeholder="Alex"
                  data-testid="contact-name-input"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-[#1C2321]">Email</span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mds-input mt-2"
                  placeholder="you@email.com"
                  data-testid="contact-email-input"
                />
              </label>
            </div>
            <label className="block mt-4">
              <span className="text-sm font-medium text-[#1C2321]">Message</span>
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mds-input mt-2 resize-none"
                placeholder="Tell me what's on your mind…"
                data-testid="contact-message-input"
              />
            </label>
            <button type="submit" disabled={loading} className="mds-btn mds-btn-primary mt-6" data-testid="contact-submit">
              {loading ? "Sending…" : (<>Send message <Send size={16} /></>)}
            </button>
            {done && (
              <p className="text-sm text-[#6B8F73] mt-4" data-testid="contact-success">
                Thanks — your message is on its way to Matt.
              </p>
            )}
          </form>
        </div>
      </section>
    </main>
  );
}
